// The portrait: archetypes, recurring symbols, and the observations Oreia holds for you.
//
// These endpoints return the sensitive parts as ciphertext (`*_cipher` columns) because the server
// stores them sealed with the user's DEK, exactly as it stores entries. Decryption happens here, in
// the browser, which is why every function in this file needs the key passed in.

import { apiDelete, apiGet, apiPost, apiPut, streamSSE } from './client';
import { openCipher } from './cipher';
import { stripRuntimeArtifacts } from './model-output';
import { parseCitations, type Citation } from './citations';

export type ArchetypeStatus =
  | 'dormant'
  | 'emerging'
  | 'developing'
  | 'strong'
  | 'core'
  | 'shifting'
  | 'confirmed';

/**
 * How loudly an archetype is currently sounding. The server stores this as a category, not a
 * number, and it is kept as one here on purpose: the underlying score is normalised so the top
 * archetype is always 1.0, which would render as a confident "100%" no matter how little writing
 * it was drawn from. A word we can stand behind beats a number we can't.
 */
export type ActivationLevel = 'dormant' | 'signal' | 'active' | 'dominant';

const ACTIVATION_LEVELS: ActivationLevel[] = ['dormant', 'signal', 'active', 'dominant'];

/** Meter fill for the blend bars — qualitative by level, matching iOS. */
export const ACTIVATION_FILL: Record<ActivationLevel, number> = {
  dominant: 100,
  active: 70,
  signal: 40,
  dormant: 15,
};

export interface Archetype {
  key: string;
  /** Human label derived from the key, e.g. "the_seeker" -> "Seeker". */
  name: string;
  level: ActivationLevel;
  /** Bar fill 0–100 derived from `level`. Never presented as a percentage of anything. */
  activation: number;
  isDominant: boolean;
  confirmed: boolean;
  status: ArchetypeStatus;
  confidence: number;
  evidenceCount: number;
  distinctEntryCount: number;
  /** Oreia's written read of this archetype, decrypted. */
  read: string;
  changeSincePrevious: number | null;
  firstDetectedAt: string | null;
  lastReinforcedAt: string | null;
}

export interface PortraitSummary {
  signals: number;
  patternsDeveloping: number;
  archetypesEmerging: number;
  archetypesConfirmed: number;
  pendingReviewCount: number;
  newEntryCount: number;
  lastComputedAt: string | null;
}

interface ArchetypeRow {
  archetype_key: string;
  /** 'dormant' | 'signal' | 'active' | 'dominant' — a category, despite the name. */
  activation_level?: string | null;
  is_dominant?: number | null;
  confirmed?: number | null;
  status?: string | null;
  confidence_score?: number | null;
  evidence_count?: number | null;
  distinct_entry_count?: number | null;
  read_cipher?: string | null;
  change_since_previous?: number | null;
  first_detected_at?: string | null;
  last_reinforced_at?: string | null;
}

interface ArchetypesResponse {
  archetypes: ArchetypeRow[];
  depth?: { tier?: string };
  summary?: Record<string, unknown>;
}

/**
 * Whether an archetype is actually sounding, rather than merely on the roster.
 *
 * All seventeen archetypes are scored on every pass, so without this the blend fills with names
 * that have never repeated in anyone's writing — a portrait of the model rather than of the person.
 * The server already sets 'dormant' for anything that hasn't cleared two distinct entries.
 */
export function isSounding(a: Archetype): boolean {
  return a.level !== 'dormant';
}

/**
 * Loudest first. Level leads, and since a level is a coarse bucket that ties constantly, the
 * evidence behind it breaks the tie — otherwise the order of the blend would come down to whatever
 * SQL happened to return first, and would visibly reshuffle between visits.
 */
export function byProminence(a: Archetype, b: Archetype): number {
  return (
    b.activation - a.activation ||
    b.confidence - a.confidence ||
    b.distinctEntryCount - a.distinctEntryCount ||
    a.name.localeCompare(b.name)
  );
}

export function parseActivation(raw: string | null | undefined): ActivationLevel {
  return ACTIVATION_LEVELS.includes(raw as ActivationLevel) ? (raw as ActivationLevel) : 'dormant';
}

/** "the_seeker" / "shadow-worker" -> "Seeker" / "Shadow Worker". */
export function archetypeLabel(key: string): string {
  return key
    .replace(/^the[_\-\s]/i, '')
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export async function fetchArchetypes(
  dek: CryptoKey,
  signal?: AbortSignal,
): Promise<{ archetypes: Archetype[]; summary: PortraitSummary; depthTier: string | null }> {
  const res = await apiGet<ArchetypesResponse>('/api/archetypes', signal);
  const rows = res.archetypes ?? [];

  const archetypes = await Promise.all(
    rows.map(async (row): Promise<Archetype> => ({
      key: row.archetype_key,
      name: archetypeLabel(row.archetype_key),
      level: parseActivation(row.activation_level),
      activation: ACTIVATION_FILL[parseActivation(row.activation_level)],
      isDominant: row.is_dominant === 1,
      confirmed: row.confirmed === 1,
      status: (row.status as ArchetypeStatus) ?? 'dormant',
      confidence: Number(row.confidence_score ?? 0),
      evidenceCount: Number(row.evidence_count ?? 0),
      distinctEntryCount: Number(row.distinct_entry_count ?? 0),
      read: stripRuntimeArtifacts(await openCipher(dek, row.read_cipher)),
      changeSincePrevious:
        row.change_since_previous === null || row.change_since_previous === undefined
          ? null
          : Number(row.change_since_previous),
      firstDetectedAt: row.first_detected_at ?? null,
      lastReinforcedAt: row.last_reinforced_at ?? null,
    })),
  );

  const s = res.summary ?? {};
  const num = (key: string) => Number(s[key] ?? 0);

  return {
    archetypes,
    depthTier: res.depth?.tier ?? null,
    summary: {
      signals: num('signals'),
      patternsDeveloping: num('patternsDeveloping'),
      archetypesEmerging: num('archetypesEmerging'),
      archetypesConfirmed: num('archetypesConfirmed'),
      pendingReviewCount: num('pendingReviewCount'),
      newEntryCount: num('newEntryCount'),
      lastComputedAt: typeof s.lastComputedAt === 'string' ? s.lastComputedAt : null,
    },
  };
}

export interface Pattern {
  canonicalId: string;
  entityType: string;
  occurrences: number;
  status: string;
  trajectory: string | null;
  /** What Oreia thinks this symbol means for you, decrypted. */
  meaning: string;
  /** The verified quotes that meaning was drawn from; empty for rows written before citations. */
  meaningCitations: Citation[];
  firstSeen: string | null;
  lastSeen: string | null;
  affirmed: boolean;
}

interface PatternRow {
  canonical_id: string;
  entity_type?: string | null;
  occurrence_count?: number | null;
  status?: string | null;
  trajectory?: string | null;
  meaning_cipher?: string | null;
  meaning_citations_cipher?: string | null;
  first_seen?: string | null;
  last_seen?: string | null;
  affirmed?: number | null;
}

/**
 * Something Oreia has noticed that is not yet a pattern, and how far off it is.
 *
 * `have` counts only sightings inside the promotion window, which is the same measure the bar is
 * judged against — so "2 of 3" means two that still count, not two since the beginning of time.
 */
export interface EmergingPattern {
  canonicalId: string;
  entityType: string;
  have: number;
  need: number;
}

interface EmergingRow {
  canonicalId?: string | null;
  type?: string | null;
  have?: number | null;
  need?: number | null;
}

export interface PatternsResult {
  patterns: Pattern[];
  emerging: EmergingPattern[];
}

export async function fetchPatterns(dek: CryptoKey, signal?: AbortSignal): Promise<PatternsResult> {
  const res = await apiGet<{ patterns: PatternRow[]; emerging?: EmergingRow[] }>('/api/patterns', signal);

  const patterns = await Promise.all(
    (res.patterns ?? []).map(async (row) => ({
      canonicalId: row.canonical_id,
      entityType: row.entity_type ?? 'symbol',
      occurrences: Number(row.occurrence_count ?? 0),
      status: row.status ?? 'significant',
      trajectory: row.trajectory ?? null,
      meaning: stripRuntimeArtifacts(await openCipher(dek, row.meaning_cipher)),
      meaningCitations: parseCitations(await openCipher(dek, row.meaning_citations_cipher)),
      firstSeen: row.first_seen ?? null,
      lastSeen: row.last_seen ?? null,
      affirmed: row.affirmed === 1,
    })),
  );

  // Absent on a server that predates this field, which must read as "nothing to say" rather than
  // as an error — the vault is the point, and the progress note is a footnote on it.
  const emerging = (res.emerging ?? []).flatMap((row) =>
    row.canonicalId
      ? [{
          canonicalId: row.canonicalId,
          entityType: row.type ?? 'symbol',
          have: Number(row.have ?? 0),
          need: Number(row.need ?? 0),
        }]
      : [],
  );

  return { patterns, emerging };
}

export interface Observation {
  id: string;
  kind: string;
  /** The observation itself, decrypted. */
  text: string;
  sourceEntryIds: string[];
  /**
   * The verified quotes behind this observation. Empty for anything written before citations
   * existed, which renders as prose with no tappable clauses until the nightly pass regenerates it.
   */
  citations: Citation[];
  trace: string | null;
  fresh: boolean;
  createdAt: string | null;
  verdict: string | null;
  /** The user said "that's true". The card stays and reads as "Fits". */
  affirmed: boolean;
  /** The user rewrote it. Their words are the text now, and the generator won't overwrite them. */
  userEdited: boolean;
}

interface ObservationRow {
  id: string;
  kind?: string | null;
  observation_cipher?: string | null;
  citations_cipher?: string | null;
  source_entry_ids?: string | null;
  trace?: string | null;
  fresh?: number | null;
  created_at?: string | null;
  verdict?: string | null;
  affirmed?: number | null;
  adjusted_at?: string | null;
}

function parseIds(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

export async function fetchObservations(dek: CryptoKey, signal?: AbortSignal): Promise<Observation[]> {
  const res = await apiGet<{ memories: ObservationRow[] }>('/api/memories', signal);
  const decrypted = await Promise.all(
    (res.memories ?? []).map(async (row) => ({
      id: row.id,
      kind: row.kind ?? 'observation',
      text: stripRuntimeArtifacts(await openCipher(dek, row.observation_cipher)),
      sourceEntryIds: parseIds(row.source_entry_ids),
      citations: parseCitations(await openCipher(dek, row.citations_cipher)),
      trace: row.trace ?? null,
      fresh: row.fresh === 1,
      createdAt: row.created_at ?? null,
      verdict: row.verdict ?? null,
      affirmed: row.affirmed === 1,
      userEdited: Boolean(row.adjusted_at),
    })),
  );
  return decrypted.filter(renderableObservation);
}

/**
 * Everything Oreia has ever held, including what was rejected and set aside.
 *
 * A separate endpoint from the active list rather than a filter on it, because the two answer
 * different questions: the active list is "what is Oreia asking me about", and this is "what have I
 * already told it". Rows carry the same shape, so a card renders from either.
 *
 * The forget verdict clears the ciphertext on the server, so those rows arrive with nothing to show
 * and are dropped by the same renderability rule as everything else — which is right. Someone who
 * asked Oreia to forget something should not find it listed in an archive.
 */
export async function fetchObservationHistory(dek: CryptoKey, signal?: AbortSignal): Promise<Observation[]> {
  const res = await apiGet<{ memories: ObservationRow[] }>('/api/memories/history', signal);
  const decrypted = await Promise.all(
    (res.memories ?? []).map(async (row) => ({
      id: row.id,
      kind: row.kind ?? 'observation',
      text: stripRuntimeArtifacts(await openCipher(dek, row.observation_cipher)),
      sourceEntryIds: parseIds(row.source_entry_ids),
      citations: parseCitations(await openCipher(dek, row.citations_cipher)),
      trace: row.trace ?? null,
      fresh: row.fresh === 1,
      createdAt: row.created_at ?? null,
      verdict: row.verdict ?? null,
      affirmed: row.affirmed === 1,
      userEdited: Boolean(row.adjusted_at),
    })),
  );
  return decrypted.filter(renderableObservation);
}

/**
 * Talk to Oreia about one thing it holds. Streams, and is premium-gated (402).
 *
 * Finished on the server and shipped on iPhone since the feature was built; the browser has simply
 * never called it. Opening with no prompt is the normal case — the server has its own first question
 * and it is the one most people would type anyway.
 */
export async function discussObservation(
  id: string,
  prompt: string | null,
  onToken: (token: string) => void,
  signal?: AbortSignal,
): Promise<void> {
  await streamSSE(
    `/api/memories/${encodeURIComponent(id)}/discuss`,
    prompt ? { prompt } : {},
    onToken,
    signal,
  );
}

/**
 * A rewording Oreia offered inside a discussion, if it offered one.
 *
 * The server's prompt tells it to prefix a proposal with "Revised:", which is the contract iOS reads
 * too. Parsed from the whole reply rather than the last line, since the model often writes a
 * sentence of reasoning after the proposal.
 */
export function proposedRevision(reply: string): string | null {
  const match = reply.match(/^\s*revised:\s*(.+)$/im);
  if (!match) return null;

  const revision = match[1].trim().replace(/^["“](.*)["”]$/s, '$1').trim();
  return revision.length > 0 ? revision : null;
}

/**
 * The invariant: an observation with nothing to point at never renders.
 *
 * The whole screen rests on being able to answer "why do you think that?" with the user's own
 * words. A claim that can't name a single entry can't be checked, argued with, or corrected — so
 * whatever it says about someone, they're stuck with it. Dropping it is the cheaper mistake.
 *
 * A card we cannot decrypt is dropped for the same reason: a blank card reads as a bug.
 */
export function renderableObservation(o: Pick<Observation, 'text' | 'sourceEntryIds'>): boolean {
  return o.text.trim().length > 0 && o.sourceEntryIds.length > 0;
}

export interface ArchetypeMovement {
  key: string;
  /** Human label derived from the key, e.g. "the_sage" -> "Sage". */
  name: string;
  direction: 'strengthening' | 'quieter' | 'new';
  confidence: number;
}

export interface PortraitChanges {
  /** Nobody has looked at the portrait yet, so there is no "since" to report against. */
  firstVisit: boolean;
  lastViewedAt: string | null;
  newSignals: number;
  newPatterns: number;
  pendingReviewCount: number;
  archetypeMovements: ArchetypeMovement[];
  hasChanges: boolean;
}

interface MovementRow {
  archetype_key?: string | null;
  change_since_previous?: string | null;
  confidence_score?: number | null;
}

/**
 * What has moved since the portrait was last looked at.
 *
 * `peek` decides whether asking also counts as looking. The endpoint records the visit by default,
 * which is right for the Self screen — you have now seen it, so it is no longer new — and wrong for
 * the dashboard, which would spend the state simply by rendering and leave the Self screen with
 * nothing to show. Passive callers must peek.
 */
export async function fetchPortraitChanges(
  { peek = false }: { peek?: boolean } = {},
  signal?: AbortSignal,
): Promise<PortraitChanges> {
  const res = await apiGet<{
    firstVisit?: boolean;
    lastViewedAt?: string | null;
    newSignals?: number;
    newPatterns?: number;
    pendingReviewCount?: number;
    archetypeMovements?: MovementRow[];
    hasChanges?: boolean;
  }>(`/api/portrait/changes${peek ? '?peek=1' : ''}`, signal);

  const archetypeMovements = (res.archetypeMovements ?? []).flatMap((row): ArchetypeMovement[] => {
    const key = row.archetype_key ?? '';
    const direction = row.change_since_previous ?? '';
    if (!key) return [];
    if (direction !== 'strengthening' && direction !== 'quieter' && direction !== 'new') return [];
    return [{
      key,
      name: archetypeLabel(key),
      direction,
      confidence: typeof row.confidence_score === 'number' ? row.confidence_score : 0,
    }];
  });

  return {
    firstVisit: res.firstVisit ?? false,
    lastViewedAt: res.lastViewedAt ?? null,
    newSignals: res.newSignals ?? 0,
    newPatterns: res.newPatterns ?? 0,
    pendingReviewCount: res.pendingReviewCount ?? 0,
    archetypeMovements,
    hasChanges: res.hasChanges ?? false,
  };
}

/**
 * Affirm, reject or defer an observation. This is how the portrait learns it got something wrong,
 * and it's the mechanism behind the promise that Oreia never insists.
 */
export async function respondToObservation(
  id: string,
  verdict: 'affirm' | 'reject' | 'defer',
  kind: 'memory' | 'entity' = 'memory',
): Promise<void> {
  await apiPost('/api/feedback/observation', { kind, id, verdict });
}

/**
 * Affirm or reject the portrait's lead archetype. `clear` puts it back to undecided.
 *
 * This is the portrait's version of the correction promise: the paragraph at the top of Self is a
 * claim like any other, so it has to be answerable. A rejected archetype stops being narrated
 * unless the evidence resurfaces on its own.
 */
export async function respondToArchetype(
  archetypeKey: string,
  verdict: 'affirm' | 'reject' | 'clear',
): Promise<void> {
  await apiPost('/api/archetypes/feedback', { archetype_key: archetypeKey, verdict });
}

/**
 * Replace an observation's wording with the user's own. Sends plaintext: the Worker re-seals it with
 * the server-wrapped copy of the DEK, and returns the card to pending so the user can affirm the
 * version that is now theirs.
 */
export async function adjustObservation(id: string, observation: string): Promise<void> {
  await apiPut(`/api/memories/${encodeURIComponent(id)}`, { observation });
}

/**
 * Forget an observation — and unlearn it. The server keeps a tombstone so the generator's exclusion
 * set can stop it being re-derived; without that, "forget" would only hide it until the next pass.
 */
export async function forgetObservation(id: string): Promise<void> {
  await apiDelete(`/api/memories/${encodeURIComponent(id)}`);
}

/** Ask the server to recompute the whole portrait. 429 means the cooldown is still running. */
export async function refreshPortrait(): Promise<'accepted' | 'cooldown' | 'failed'> {
  try {
    await apiPost('/api/analysis/refresh', {});
    return 'accepted';
  } catch (err) {
    if (err && typeof err === 'object' && 'status' in err && err.status === 429) return 'cooldown';
    return 'failed';
  }
}

/** Why a run found nothing. Tokens, not sentences — the copy lives in the UI, the facts here. */
export type NothingNewReason =
  | 'paused'
  | 'locked'
  | 'nothing_to_read'
  | 'below_threshold'
  | 'awaiting_quote'
  | 'more_queued';

export interface NearingPattern {
  label: string;
  type: string;
  have: number;
  need: number;
}

export interface AnalysisRun {
  status: 'running' | 'done' | 'failed';
  startedAt: string;
  finishedAt: string | null;
  entriesRead: number;
  newPatterns: number;
  newMemories: number;
  newMeanings: number;
  newLinks: number;
  movements: ArchetypeMovement[];
  nearing: NearingPattern[];
  reasons: NothingNewReason[];
  pendingEntries: number;
  foundSomething: boolean;
}

interface RunResponse {
  run?: (Omit<AnalysisRun, 'movements'> & {
    movements?: Array<{ key?: string; direction?: string }>;
  }) | null;
}

/**
 * What the last update actually did.
 *
 * Separate from `fetchPortraitChanges`, which answers "what's new since you last looked" and is
 * spent by looking. This answers "what did the button I just pressed do" — including, and
 * especially, "nothing, and here is why".
 */
export async function fetchAnalysisRun(signal?: AbortSignal): Promise<AnalysisRun | null> {
  const res = await apiGet<RunResponse>('/api/analysis/refresh', signal);
  const run = res.run;
  if (!run) return null;
  return {
    status: run.status ?? 'done',
    startedAt: run.startedAt,
    finishedAt: run.finishedAt ?? null,
    entriesRead: run.entriesRead ?? 0,
    newPatterns: run.newPatterns ?? 0,
    newMemories: run.newMemories ?? 0,
    newMeanings: run.newMeanings ?? 0,
    newLinks: run.newLinks ?? 0,
    movements: (run.movements ?? []).flatMap((m): ArchetypeMovement[] => {
      const key = m.key ?? '';
      const direction = m.direction ?? '';
      if (!key) return [];
      if (direction !== 'strengthening' && direction !== 'quieter' && direction !== 'new') return [];
      return [{ key, name: archetypeLabel(key), direction, confidence: 0 }];
    }),
    nearing: run.nearing ?? [],
    reasons: run.reasons ?? [],
    pendingEntries: run.pendingEntries ?? 0,
    foundSomething: run.foundSomething ?? false,
  };
}
